/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: BrandingFormView.tsx
 🗓️ Created: 2025-01-06
 🧩 Description:
    Branding configuration form with logo upload, color picker,
    and interface customization options for tenant branding.

 🔧 Notes:
    - Supports logo and favicon upload
    - Color picker for primary and secondary colors
    - Interface text customization
    - Real-time preview of branding changes
    - Form validation and error handling

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Palette, Settings, Image, LogIn } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useFixedActionBar } from '../../hooks/useFixedActionBar';
import { AppInfoTab } from './AppInfoTab';
import { LogosTab } from './LogosTab';
import { LoginDesignTab } from './LoginDesignTab';
import { TabProps } from './types';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { ModuleFormHeader } from '../../components/layouts';
import { useBrandingData } from './hooks/useBrandingData';
import { useBrandingForm } from './hooks/useBrandingForm';
import { useFileUpload } from './hooks/useFileUpload';
import { useBrandingSubmit } from './hooks/useBrandingSubmit';
import { IMAGE_FIELDS } from './constants';

const BrandingFormView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const formRef = useRef<HTMLFormElement | null>(null);

  useFixedActionBar(true);

  const [activeTab, setActiveTab] = useState<'app-info' | 'logos' | 'login-design'>('app-info');

  // Load branding data
  const { tenant, brandingConfig: initialConfig, loading } = useBrandingData(id);

  // Form state management
  const {
    formData,
    errors,
    hasChanges,
    hasErrors,
    handleInputChange,
    validateForm,
    resetForm,
  } = useBrandingForm(initialConfig);

  // File upload handling
  const {
    uploadingField,
    previews,
    handleFileUpload,
    handleRemoveFile,
    setPreview,
  } = useFileUpload((field, path) => {
    handleInputChange(field, path);
  });

  // Form submission
  const { saving, handleSubmit: handleSubmitForm } = useBrandingSubmit(
    tenant?.id ?? null,
    formData,
    (config) => {
      resetForm(config);
    }
  );

  // Initialize previews when config loads
  useEffect(() => {
    if (initialConfig) {
      IMAGE_FIELDS.forEach((field) => {
        setPreview(field, initialConfig[field] ?? null);
      });
    }
  }, [initialConfig, setPreview]);

  // Clean up location state
  useEffect(() => {
    if ((location.state as any)?.fromBranding) {
      navigate(location.pathname, { replace: true, state: undefined });
    }
  }, [location.pathname, location.state, navigate]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }
    await handleSubmitForm(hasChanges);
  };

  const tabProps: TabProps = {
    formData,
    errors,
    onInputChange: handleInputChange,
    onFileUpload: handleFileUpload,
    onRemoveFile: handleRemoveFile,
    previews,
    uploadingField,
  };

  if (loading) {
    return (
      <div className="p-6">
        <ModuleFormHeader
          icon={<Palette className="h-5 w-5 text-primary" />}
          title={t('branding.editBranding')}
          backButtonText={t('common.list')}
          onBack={() => navigate('/admin/system-settings/branding')}
        />
        <div className="mt-8">
          <div className="h-64 animate-pulse rounded-lg border border-dashed border-border bg-muted/30" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <ModuleFormHeader
        icon={<Palette className="h-5 w-5 text-primary" />}
        title={tenant ? `${t('branding.editBranding')} – ${tenant.name}` : t('branding.editBranding')}
        backButtonText={t('common.list')}
        onBack={() => navigate('/admin/system-settings/branding')}
      />

      <div className="mt-8">
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
          <Tabs value={activeTab} onValueChange={value => setActiveTab(value as typeof activeTab)}>
            <TabsList className="mb-2 w-full justify-start rounded-lg border border-border bg-card p-1">
              <TabsTrigger value="app-info" icon={<Settings className="h-4 w-4" />}>
                {t('branding.appInformation')}
              </TabsTrigger>
              <TabsTrigger value="logos" icon={<Image className="h-4 w-4" />}>
                {t('branding.logosAndIcons')}
              </TabsTrigger>
              <TabsTrigger value="login-design" icon={<LogIn className="h-4 w-4" />}>
                {t('branding.loginDesign')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="app-info" className="mt-2 space-y-3">
              <AppInfoTab {...tabProps} />
            </TabsContent>

            <TabsContent value="logos" className="mt-2 space-y-3">
              <LogosTab {...tabProps} />
            </TabsContent>

            <TabsContent value="login-design" className="mt-2 space-y-3">
              <LoginDesignTab {...tabProps} />
            </TabsContent>
          </Tabs>
        </form>
      </div>

      <FixedActionBar
        onSave={() => formRef.current?.requestSubmit()}
        onCancel={() => resetForm(initialConfig)}
        saveDisabled={saving || hasErrors || loading}
        isLoading={saving}
        saveText={saving ? t('common.saving') : t('common.save')}
        cancelText={t('common.cancel')}
      />
    </div>
  );
};

export default BrandingFormView;
